import { type HealthIssue } from "@u/contentHealth";
import { cn } from "@u/cn";
import { Checkbox } from "@ui/checkbox";
import {
  AlertOctagon,
  AlertTriangle,
  Eye,
  EyeOff,
  Info,
} from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const SEV_ICON = {
  critical: AlertOctagon,
  warning: AlertTriangle,
  info: Info,
} as const;
const SEV_COLOR = {
  critical: "text-destructive-strong",
  warning: "text-warning-strong",
  info: "text-muted-foreground",
} as const;

function HealthRowInner({
  issue,
  onNavigate,
  onDismiss,
  onRestore,
  selected,
  onSelectChange,
}: {
  issue: HealthIssue;
  onNavigate?: () => void;
  /** Hide this aviso (deliberate). Omitted for already-hidden rows. */
  onDismiss?: (key: string) => void;
  /** Un-hide a hidden aviso. */
  onRestore?: (key: string) => void;
  /** Selection checkbox (bulk actions, full page). Takes the issue id so the
   *  parent can pass one stable callback instead of a fresh closure per row. */
  selected?: boolean;
  onSelectChange?: (id: string, checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Icon = SEV_ICON[issue.severity];

  // Business avisos aren't hideable (they're time-derived, see useContentHealth)
  // — no dismiss control and no selection checkbox for the bulk hide.
  const hideable = issue.product !== "business";

  return (
    <div className="group/row flex items-center gap-1 pr-2 hover:bg-muted/50 transition-colors">
      {onSelectChange && hideable && (
        <span className="pl-3">
          <Checkbox
            checked={selected}
            onCheckedChange={(c) => onSelectChange(issue.id, c === true)}
            aria-label={t("select")}
          />
        </span>
      )}
      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          navigate(issue.href);
        }}
        className="min-w-0 flex-1 flex items-center gap-3 pl-4 py-3 text-left"
      >
        <Icon
          size={16}
          strokeWidth={1.75}
          className={cn("shrink-0", SEV_COLOR[issue.severity])}
        />
        <span
          className={cn(
            "min-w-0 flex-1 text-sm",
            onRestore ? "text-muted-foreground line-through" : "text-foreground",
          )}
        >
          {t(
            issue.msgKey ?? `health.msg.${issue.code}`,
            issue.vars as Record<string, unknown>,
          )}
        </span>
      </button>
      {onDismiss && hideable && (
        <button
          type="button"
          onClick={() => onDismiss(issue.id)}
          aria-label={t("health.dismiss")}
          title={t("health.dismiss")}
          className="shrink-0 p-1 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted opacity-0 group-hover/row:opacity-100 focus:opacity-100 transition"
        >
          <EyeOff size={14} />
        </button>
      )}
      {onRestore && (
        <button
          type="button"
          onClick={() => onRestore(issue.id)}
          aria-label={t("health.restore")}
          title={t("health.restore")}
          className="shrink-0 p-1 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted transition"
        >
          <Eye size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * Rows are pure given their props, and the avisos list re-renders wholesale on
 * every filter/selection change — memo keeps that to the rows that changed.
 */
export const HealthRow = memo(HealthRowInner);
